import { lazy } from "react";
import { HeartPulse, Microscope, Pill, Scissors, ShieldAlert, TestTube } from "lucide-react";
import type { AnimalDetail } from "../../contract";
import { defineWebModule } from "../../web/module";
import { HEALTH_TYPES } from "./contract";
import { AnimalHealth, healthState } from "./web/AnimalHealth";
import { isHealthProduct } from "./web/Supplies";
import "./web/health.css";

// Pages load when opened, so the app starts fast on a phone with weak signal.
const LotTrace = lazy(() => import("./web/Supplies").then((m) => ({ default: m.LotTrace })));
const ProductLabel = lazy(() => import("./web/Supplies").then((m) => ({ default: m.ProductLabel })));
const DoseDetail = lazy(() => import("./web/AnimalHealth").then((m) => ({ default: m.DoseDetail })));
const HealthWorkspace = lazy(() => import("./web/Workspace").then((m) => ({ default: m.HealthWorkspace })));
const ProcedureForm = lazy(() => import("./web/ProcedureForm").then((m) => ({ default: m.ProcedureForm })));
const RoundJob = lazy(() => import("./web/RoundJob").then((m) => ({ default: m.RoundJob })));
const TreatmentForm = lazy(() => import("./web/TreatmentForm").then((m) => ({ default: m.TreatmentForm })));

const current = (a: AnimalDetail) => a.status === "active" || a.status === "missing";
const sick = (a: AnimalDetail) => !!healthState(a)?.sick;
const bullish = (a: AnimalDetail) => a.sex === "male" && !a.castrated;

export default defineWebModule({
  id: "health",
  icons: { pill: Pill, scissors: Scissors, testTube: TestTube, microscope: Microscope, shieldAlert: ShieldAlert, heartPulse: HeartPulse },
  events: {
    [HEALTH_TYPES.treatment]: { Form: TreatmentForm, Detail: DoseDetail },
    [HEALTH_TYPES.vaccination]: { Form: TreatmentForm, Detail: DoseDetail },
    [HEALTH_TYPES.procedure]: { Form: ProcedureForm },
  },
  animalSections: [{ id: "health", label: "Health", order: 30, show: (a) => !!a.facets.health, Component: AnimalHealth }],
  animalActions: [
    // A sick animal's recheck and treatment come first; otherwise health sits among other modules' actions.
    { id: "health-recheck", label: "Recheck", icon: "thermometer", eventType: HEALTH_TYPES.sickness, when: (a) => current(a) && sick(a), data: (a) => ({ condition: healthState(a)?.sickness?.condition }), priority: 85 },
    { id: "health-treat", label: "Treat", icon: "pill", eventType: HEALTH_TYPES.treatment, when: current, data: (a) => (sick(a) && healthState(a)?.sickness?.condition ? { reason: healthState(a)!.sickness!.condition } : {}), priority: 60 },
    { id: "health-vaccinate", label: "Vaccinate", icon: "syringe", eventType: HEALTH_TYPES.vaccination, when: current, priority: 48 },
    { id: "health-bse", label: "Bull exam (BSE)", icon: "microscope", eventType: HEALTH_TYPES.bse, when: (a) => current(a) && bullish(a) && a.class === "bull", priority: 46 },
    { id: "health-sick", label: "Note sickness", icon: "thermometer", eventType: HEALTH_TYPES.sickness, when: (a) => current(a) && !sick(a), priority: 42 },
    { id: "health-castrate", label: "Castrate", icon: "scissors", eventType: HEALTH_TYPES.procedure, data: () => ({ kind: "castration" }), when: (a) => current(a) && bullish(a) && a.class === "bull_calf", priority: 40 },
  ],
  workspaces: { health: HealthWorkspace },
  jobs: { "health.round": RoundJob },
  stockSections: [
    { id: "health-label", label: "Label and withdrawal", order: 10, show: isHealthProduct, Component: ProductLabel },
    { id: "health-trace", label: "Given to", order: 20, show: isHealthProduct, Component: LotTrace },
  ],
});
